import { EntryDialog } from '@/client/components/timesheets/EntryDialog';
import type { EntryForm } from '@/client/components/timesheets/EntryFormFields';
import { MonthView } from '@/client/components/timesheets/MonthView';
import { SubmitDialog } from '@/client/components/timesheets/SubmitDialog';
import { WeekGrid } from '@/client/components/timesheets/WeekGrid';
import { WeekNav } from '@/client/components/timesheets/WeekNav';
import { WeekSummaryStrip } from '@/client/components/timesheets/WeekSummaryStrip';
import { Button } from '@/client/components/ui/button';
import { getMonthRange, getWeekRange } from '@/client/lib/dates';
import { isRangeCovered } from '@/client/lib/timesheets';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

type Entry = RouterOutputs['timesheets']['listEntries'][number];

export const Route = createFileRoute('/_app/timesheets/')({
  component: TimesheetsPage,
});

function TimesheetsPage() {
  const utils = trpc.useUtils();
  const [view, setView] = useState<'week' | 'month'>('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEntry, setDialogEntry] = useState<Entry | null>(null);
  const [dialogDate, setDialogDate] = useState<string | undefined>(undefined);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [entryError, setEntryError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const weekRange = getWeekRange(weekOffset);
  const monthRange = getMonthRange(monthOffset);
  const range = view === 'week' ? weekRange : monthRange;

  const [yearStr, monthStr] = monthRange.from.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);

  const { data: me } = trpc.user.me.useQuery();
  const { data: entries = [] } = trpc.timesheets.listEntries.useQuery({
    date_from: range.from,
    date_to: range.to,
  });
  const { data: submissions = [] } = trpc.timesheets.listSubmissions.useQuery({});
  const { data: myProjects = [] } = trpc.timesheets.myProjects.useQuery();

  function invalidate() {
    utils.timesheets.listEntries.invalidate();
    utils.timesheets.listSubmissions.invalidate();
  }

  const createMutation = trpc.timesheets.createEntry.useMutation({
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
    },
    onError: (e) => setEntryError(e.message),
  });
  const updateMutation = trpc.timesheets.updateEntry.useMutation({
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
    },
    onError: (e) => setEntryError(e.message),
  });
  const deleteMutation = trpc.timesheets.deleteEntry.useMutation({
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
    },
  });
  const submitMutation = trpc.timesheets.submit.useMutation({
    onSuccess: () => {
      invalidate();
      setSubmitOpen(false);
    },
    onError: (e) => setSubmitError(e.message),
  });

  function handleAddOnDay(date: string) {
    setDialogDate(date);
    setDialogEntry(null);
    setEntryError(null);
    setDialogOpen(true);
  }

  function handleEdit(entry: Entry) {
    setDialogEntry(entry);
    setDialogDate(undefined);
    setEntryError(null);
    setDialogOpen(true);
  }

  function handleEntrySubmit(form: EntryForm) {
    setEntryError(null);
    if (dialogEntry) {
      updateMutation.mutate({
        id: dialogEntry.id,
        project_id: form.project_id,
        date: form.date,
        description: form.description,
        hours: Number(form.hours),
      });
    } else {
      createMutation.mutate({
        project_id: form.project_id,
        date: form.date,
        description: form.description,
        hours: Number(form.hours),
      });
    }
  }

  function handleSubmitWeek(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    submitMutation.mutate({ date_from: weekRange.from, date_to: weekRange.to });
  }

  const weekSubmitted = isRangeCovered(weekRange.from, weekRange.to, submissions);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <WeekNav
            label={range.label}
            onPrev={() =>
              view === 'week'
                ? setWeekOffset((o) => o - 1)
                : setMonthOffset((o) => o - 1)
            }
            onNext={() =>
              view === 'week'
                ? setWeekOffset((o) => o + 1)
                : setMonthOffset((o) => o + 1)
            }
            onToday={() =>
              view === 'week' ? setWeekOffset(0) : setMonthOffset(0)
            }
            showToday={view === 'week' ? weekOffset !== 0 : monthOffset !== 0}
            todayLabel={view === 'week' ? 'This week' : 'This month'}
          />
          <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
            {(['week', 'month'] as const).map((tab) => (
              <button
                type="button"
                key={tab}
                onClick={() => setView(tab)}
                className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  view === tab
                    ? 'bg-card text-foreground shadow-soft'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {view === 'week' &&
          (weekSubmitted ? (
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
              Submitted
            </span>
          ) : (
            entries.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSubmitError(null);
                  setSubmitOpen(true);
                }}
              >
                Submit for approval
              </Button>
            )
          ))}
      </div>

      {view === 'week' ? (
        <>
          <WeekSummaryStrip
            entries={entries}
            weekFrom={weekRange.from}
            weekSubmitted={weekSubmitted}
          />
          <WeekGrid
            weekFrom={weekRange.from}
            entries={entries}
            submissions={submissions}
            onAdd={handleAddOnDay}
            onEdit={handleEdit}
          />
        </>
      ) : (
        <MonthView
          key={`${year}-${month}`}
          year={year}
          month={month}
          entries={entries}
          submissions={submissions}
          onAdd={handleAddOnDay}
          onEdit={handleEdit}
        />
      )}

      <EntryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entry={dialogEntry}
        projects={myProjects}
        onSubmit={handleEntrySubmit}
        isPending={dialogEntry ? updateMutation.isPending : createMutation.isPending}
        error={entryError}
        defaultDate={dialogDate}
        onDelete={dialogEntry ? () => deleteMutation.mutate({ id: dialogEntry.id }) : undefined}
        isDeleting={deleteMutation.isPending}
        isAdmin={me?.role === 'ADMIN'}
      />

      <SubmitDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        dateFrom={weekRange.from}
        dateTo={weekRange.to}
        onDateFromChange={() => {}}
        onDateToChange={() => {}}
        onSubmit={handleSubmitWeek}
        isPending={submitMutation.isPending}
        error={submitError}
      />
    </div>
  );
}
